import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api, qs } from "../api/client";
import { keys } from "../api/hooks";
import { rulesResponseSchema, type Mode } from "../../api/contracts";
import { DEFAULT_RULE_SET } from "../../btb/rules";
import { coverageSentence, describeRuleSet, exampleSentences, lockSentence, ownBlocksSentence, turnoverSentence, workedExample } from "../lib/rules";
import { ruleScopeLabel } from "../lib/locations";
import { Button, CloseButton, Panel, cx } from "./ui";
import { UntouchedNote } from "./UntouchedNote";
import { trackAction } from "../lib/usage-track";

export const guideKey = (email: string) => `btb.guide.closed:${email.trim().toLowerCase()}`;

/** Storage can throw (private mode, disabled storage); then the guide opens and closing it lasts the session. */
export function readGuideClosed(email: string): boolean {
  try { return localStorage.getItem(guideKey(email)) !== null; } catch { return false; }
}
export function writeGuideClosed(email: string, closed: boolean): void {
  try { if (closed) localStorage.setItem(guideKey(email), new Date().toISOString()); else localStorage.removeItem(guideKey(email)); } catch { /* session only */ }
}

const link = "font-semibold text-hs-text underline underline-offset-2";

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-4">
      <h3 className="mb-2 text-sm font-bold text-hs-text">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** The Figma bullet: a crimson dot beside muted copy. */
function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span aria-hidden className="mt-[7px] h-2 w-2 shrink-0 rounded-full bg-hs-primary" />
          <span className="min-w-0 flex-1">{item}</span>
        </li>
      ))}
    </ul>
  );
}

const STEPS = [
  { title: "Plan", text: (days: number) => <>Looks {days} days ahead at the open time before the first appointment and after the last one.</> },
  { title: "Place", text: () => <>Adds, resizes or removes blocks on staff calendars to match <Link to="/rules" className="text-hs-primary underline underline-offset-2">your rules</Link>.</> },
  { title: "Adjust", text: () => <>Re-plans every night, and within a minute or two when a booking is made, moved or cancelled.</> },
];

/**
 * The owner's reference for how the tool works, at the top of My locations. The card always shows the
 * three steps; "Open full guide" slides the full guide in from the right. The guide opens on the first
 * visit, and the open/closed choice is remembered per email. The numbers are the owner's own rules (first
 * owner id), falling back to the corporate default while they load or if they cannot be read.
 */
export function HowItWorksGuide({ email, ownerId, mode }: { email: string; ownerId: string | null; mode: Mode }) {
  const [open, setOpen] = useState(() => !readGuideClosed(email));
  const bodyId = useId();
  const rules = useQuery({
    queryKey: keys.rules("owner", ownerId), enabled: ownerId !== null,
    queryFn: () => api(`/api/rules${qs({ scope: "owner", key: ownerId })}`, rulesResponseSchema),
  });
  const effective = rules.data?.effective;
  const r = effective?.rules ?? DEFAULT_RULE_SET;
  // Whether the drawer was opened by the button (then it takes focus) or by the first-visit default (then it does not).
  const opened = useRef(false);
  const set = (next: boolean) => { trackAction(next ? "guide_opened" : "guide_closed"); opened.current = next; writeGuideClosed(email, !next); setOpen(next); };
  const example = workedExample(r);

  return (
    <>
      <Panel className="mb-7" padding="none" title="How BTB blocks work" subtitle="Open time at the start or end of a shift gets a short “Auto - BTB” block, so online bookings land next to appointments."
        actions={<Button variant="secondary" size="sm" aria-expanded={open} aria-controls={bodyId} onClick={() => set(true)}>Open full guide</Button>}>
        <ol className="grid gap-6 border-t border-hs-border px-6 py-4 [counter-reset:step] md:grid-cols-3 md:gap-8">
          {STEPS.map((s) => (
            // The number is a CSS counter, not text, so it never collides with a count in the table below.
            <li key={s.title} className="flex items-start gap-2 [counter-increment:step] before:flex before:h-[30px] before:w-[30px] before:shrink-0 before:items-center before:justify-center before:rounded-full before:bg-hs-primary-soft before:text-[15px] before:font-medium before:text-hs-primary before:content-[counter(step)]">
              <div className="min-w-0 pt-0.5">
                <p className="text-base font-semibold text-hs-text">{s.title}</p>
                <p className="mt-1 text-sm text-hs-text">{s.text(r.lookaheadDays)}</p>
              </div>
            </li>
          ))}
        </ol>
      </Panel>
      {open && (
        <GuideDrawer id={bodyId} onClose={() => set(false)} focusOnOpen={opened.current}>
          <Section id="guide-how" title="How the app works">
            <Bullets items={[
              <>For every location that is on, the app plans each staff member's shifts for the next {r.lookaheadDays} days (your planning window, set on <Link to="/rules" className={link}>Rules</Link>): it looks at the open time before the first appointment and after the last one, and places, resizes or removes BTB blocks to match your rules.</>,
              <>Every night it re-plans the whole window. When a booking is made, moved or cancelled in Boulevard, that location's day is re-planned within a minute or two.</>,
              <>Saving rules re-plans the locations they apply to right away.</>,
              <>Staff with the "Training Account" role are never planned. This goes by the role in Boulevard, not the name. If a training login is set up as an aesthetician, add it on the <Link to="/staff" className={link}>Staff</Link> page.</>,
              <>Everything the app does shows up on the <Link to="/activity" className={link}>Activity</Link> page, with the reason for each change.</>,
            ]} />
          </Section>
          <Section id="guide-switch" title="The switch on each location">
            <Bullets items={[
              <><span className="font-semibold text-hs-text">On:</span> the app manages BTB blocks for that location's staff, as described above. You confirm before it turns on.</>,
              <><span className="font-semibold text-hs-text">Off:</span> the app stops planning that location. Blocks already on the calendar stay until someone removes them.</>,
              <><span className="font-semibold text-hs-text">Pause:</span> keeps the location on but makes no calendar changes until you resume.</>,
              <><span className="font-semibold text-hs-text">Preview:</span> shows what the planning window would look like, without changing anything. Use it before you turn a location on.</>,
            ]} />
            {mode === "shadow" && <p>Preview mode is on for the whole system right now: even locations that are on only plan, and nothing is written to Boulevard until corporate switches to live.</p>}
          </Section>
          <Section id="guide-rules" title={`Your rules${effective ? ` (${ruleScopeLabel(effective.scope)})` : ""}`}>
            <Bullets items={describeRuleSet(r)} />
            {example && <p>{exampleSentences(example, r).join(" ")}</p>}
            <p>A location can have its own rules that replace these. <Link to="/rules" className={link}>Review or change rules</Link></p>
          </Section>
          <Section id="guide-never" title="What never changes">
            <Bullets items={[
              <><span className="font-semibold text-hs-text">Cleaning and setup.</span> {turnoverSentence(r.turnoverMaxMinutes)} {ownBlocksSentence(r.turnoverMaxMinutes, r.adoptManualBtb)}</>,
              ...(r.keepSomeoneIn ? [<><span className="font-semibold text-hs-text">Someone in the salon.</span> {coverageSentence(r.keepSomeoneIn)}</>] : []),
              <><span className="font-semibold text-hs-text">Schedule locks.</span> {lockSentence(r)}</>,
            ]} />
            <UntouchedNote />
          </Section>
        </GuideDrawer>
      )}
    </>
  );
}

const TABS = [
  { id: "guide-how", label: "How it works" },
  { id: "guide-switch", label: "The switch" },
  { id: "guide-never", label: "What never changes" },
];

/**
 * The Figma guide panel: slides in from the right, 586px wide, over a dimmed page. The three tabs are jump
 * links into one scrolling body (every section stays rendered), so nothing an owner needs is hidden behind a
 * tab. A complementary panel, not a modal: it opens by itself on an owner's first visit, so it must not trap
 * focus or steal it then; opened from the button it takes focus and gives it back on close. Escape, the
 * backdrop and the "x" close it.
 */
function GuideDrawer({ id, onClose, focusOnOpen, children }: { id: string; onClose: () => void; focusOnOpen: boolean; children: ReactNode }) {
  const panel = useRef<HTMLElement>(null);
  const [tab, setTab] = useState(TABS[0].id);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const opener = document.activeElement as HTMLElement | null;
    if (focusOnOpen) panel.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") close.current(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previous; document.removeEventListener("keydown", onKey); if (focusOnOpen) opener?.focus?.(); };
  }, [focusOnOpen]);
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
  const jump = (target: string) => {
    setTab(target);
    panel.current?.querySelector(`#${target}`)?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  };
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside ref={panel} id={id} tabIndex={-1} aria-label="How BTB blocks work guide" onKeyDown={onKeyDown}
        className="absolute inset-y-0 right-0 flex w-full max-w-[586px] flex-col bg-hs-surface shadow-lg outline-none">
        <div className="relative shrink-0 px-8 pt-8">
          <CloseButton label="Close guide" onClick={onClose} className="absolute right-6 top-6" />
          <h2 className="pr-10 text-lg font-bold text-hs-text">How BTB blocks work</h2>
          <h3 className="sr-only">What a BTB block is</h3>
          <p className="mt-2 max-w-[451px] text-sm font-medium text-hs-text-muted">A BTB (block-til-busy) block sits on a staff member's Boulevard calendar, titled “Auto - BTB”, at the start or end of a shift. It keeps online bookings from landing far from the appointments already on the book and leaving stranded gaps. Once the day fills up, it comes off so that time can be booked.</p>
          <div className="mt-6 flex gap-6 overflow-x-auto border-b border-hs-border" role="tablist" aria-label="Guide sections">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => jump(t.id)}
                className={cx("-mb-px whitespace-nowrap border-b-2 pb-2 text-sm font-semibold", tab === t.id ? "border-hs-primary text-hs-text" : "border-transparent text-hs-text hover:text-hs-primary")}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-8 pb-8 pt-6 text-sm font-medium leading-5 text-hs-text-muted">{children}</div>
      </aside>
    </div>
  );
}
